/**
 * 温馨治愈风格登录日志页面
 */

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  message,
  Tag,
  DatePicker,
  Select,
  Row,
  Col,
  Typography
} from 'antd';
import {
  DeleteOutlined,
  SearchOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  FilterOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { getLoginLogPage, deleteLoginLogs } from '../services/api/loginLog';
import { LoginLogStatus } from '../types/loginLog';
import SearchBar from '../components/common/SearchBar';
import EmptyWarm from '../components/common/EmptyWarm';
import LoadingWarm from '../components/common/LoadingWarm';
import './LoginLog.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const LoginLog: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterForm] = Form.useForm();
  const [filterVisible, setFilterVisible] = useState(false);
  const [searchParams, setSearchParams] = useState({
    username: '',
    status: undefined as number | undefined,
    startTime: '',
    endTime: ''
  });

  // 获取日志列表
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await getLoginLogPage({
        current,
        size: pageSize,
        ...searchParams
      });
      setLogs(res.records || []);
      setTotal(res.total || 0);
    } catch (error) {
      console.error('获取登录日志失败:', error);
      message.error('获取登录日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [current, pageSize, searchParams]);

  // 删除选中的日志
  const handleDeleteSelected = async () => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除选中的登录日志吗？',
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const selectedIds = logs.filter(log => log.selected).map(log => log.id);
          if (selectedIds.length === 0) {
            message.warning('请先选择要删除的日志');
            return;
          }
          await deleteLoginLogs(selectedIds);
          message.success('日志删除成功');
          fetchLogs();
        } catch (error) {
          console.error('删除日志失败:', error);
          message.error('删除日志失败');
        }
      }
    });
  };

  // 应用筛选条件
  const handleApplyFilter = async () => {
    try {
      const values = await filterForm.validateFields();
      const startDate = values.dateRange?.[0];
      const endDate = values.dateRange?.[1];

      setSearchParams({
        username: values.username || '',
        status: values.status !== undefined ? Number(values.status) : undefined,
        startTime: startDate ? startDate.format('YYYY-MM-DD HH:mm:ss') : '',
        endTime: endDate ? endDate.format('YYYY-MM-DD HH:mm:ss') : ''
      });
      setCurrent(1);
      setFilterVisible(false);
    } catch (error) {
      console.error('筛选失败:', error);
      message.error('筛选条件填写有误');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '👤 用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120
    },
    {
      title: '🔐 登录类型',
      dataIndex: 'loginType',
      key: 'loginType',
      width: 100,
      render: (type: string) => type || '账号登录'
    },
    {
      title: '📊 状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: number) => (
        <Tag icon={status === 1 ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={status === 1 ? 'success' : 'error'}>
          {status === 1 ? '成功' : '失败'}
        </Tag>
      )
    },
    {
      title: '🌐 IP 地址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 140,
      ellipsis: true
    },
    {
      title: '📍 地点',
      dataIndex: 'ipLocation',
      key: 'ipLocation',
      width: 150,
      ellipsis: true
    },
    {
      title: '💻 浏览器',
      dataIndex: 'browser',
      key: 'browser',
      width: 120,
      ellipsis: true,
      render: (browser: string) => browser || '-'
    },
    {
      title: '🖥️ 系统',
      dataIndex: 'os',
      key: 'os',
      width: 120,
      ellipsis: true,
      render: (os: string) => os || '-'
    },
    {
      title: '📅 登录时间',
      dataIndex: 'loginTime',
      key: 'loginTime',
      width: 180,
      render: (time: string) => (
        <Text type="secondary">{time}</Text>
      )
    }
  ];

  return (
    <div className="login-log-container">
      <Card
        className="warm-card"
        title={
          <Space>
            <SafetyOutlined className="login-log-title-icon" />
            <span>登录日志</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterVisible(true)}
            >
              🌸 筛选
            </Button>
          </Space>
        }
        bordered={false}
      >
        <div className="login-log-search-bar">
          <SearchBar
            placeholder="搜索用户名..."
            onSearch={(value) => {
              setSearchParams({ ...searchParams, username: value });
              setCurrent(1);
            }}
            width={300}
          />
        </div>

        {loading ? (
          <LoadingWarm type="cloud" tip="日志数据正在路上~" />
        ) : (
          <Table
            dataSource={logs}
            columns={columns}
            rowKey="id"
            pagination={{
              current,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, size) => {
                setCurrent(page);
                setPageSize(size || 10);
              }
            }}
            scroll={{ x: 1000 }}
            locale={{ emptyText: <EmptyWarm type="search" /> }}
          />
        )}
      </Card>

      {/* 筛选条件模态框 */}
      <Modal
        title="🔍 筛选条件"
        open={filterVisible}
        onOk={handleApplyFilter}
        onCancel={() => setFilterVisible(false)}
        okText="应用"
        cancelText="取消"
        width={600}
        className="warm-modal"
      >
        <Form form={filterForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="👤 用户名"
              >
                <Input placeholder="请输入用户名" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="status"
                label="📊 状态"
              >
                <Select placeholder="请选择状态" allowClear>
                  <Option value={1}>✅ 成功</Option>
                  <Option value={0}>❌ 失败</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="dateRange"
                label="📅 时间范围"
              >
                <RangePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder={['开始时间', '结束时间']}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default LoginLog;
